/**
 * Structured data (JSON-LD) builders for SEO rich results.
 * Feed the output into useSEO({ jsonLd }).
 */

const ORIGIN = "https://melbiwellness.com";

const BLOCK_TAGS = new Set(["p", "li", "ul", "ol", "div", "br"]);

// Plain text of a React node (string, array or JSX) — keeps FAQ schema identical to visible copy.
export function nodeText(node) {
  const walk = (n) => {
    if (n == null || typeof n === "boolean") return "";
    if (typeof n === "string" || typeof n === "number") return String(n);
    if (Array.isArray(n)) return n.map(walk).join("");
    const inner = walk(n.props?.children);
    return typeof n.type === "string" && BLOCK_TAGS.has(n.type) ? ` ${inner} ` : inner;
  };
  return walk(node).replace(/\s+/g, " ").trim();
}

export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": ["Organization", "MedicalBusiness"],
    "@id": `${ORIGIN}/#organization`,
    name: "Melbi Wellness",
    url: ORIGIN,
    email: "hello@melbiwellness.com",
    logo: `${ORIGIN}/android-chrome-512x512.png`,
    image: `${ORIGIN}/og/default.png`,
    description:
      "Online clinician-guided wellness care. Compounded medications, licensed U.S. pharmacy fulfillment, available in all 50 states.",
    priceRange: "$$",
    medicalSpecialty: [
      "PreventiveMedicine",
      "Dermatology",
      "Endocrinology",
    ],
    areaServed: {
      "@type": "Country",
      name: "United States",
    },
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer support",
      email: "hello@melbiwellness.com",
      availableLanguage: ["English"],
    },
    sameAs: [
      "https://www.instagram.com/melbiwellness",
      "https://www.facebook.com/melbiwellness",
    ],
  };
}

export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Melbi Wellness",
    url: ORIGIN,
    potentialAction: {
      "@type": "SearchAction",
      target: `${ORIGIN}/treatments?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
}

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      name: it.name,
      item: it.url.startsWith("http") ? it.url : `${ORIGIN}${it.url}`,
    })),
  };
}

/**
 * Product schema for a treatment page.
 * `treatment` is a treatments.js record. Uses pricing.startingAt (or
 * pricing.options[0].price for multi-option products) as the offer price.
 */
export function productSchema(treatment, slug) {
  if (!treatment) return null;
  const url = `${ORIGIN}/treatments/${slug}`;
  const primaryImage = `${ORIGIN}/vials/${treatment.primaryVialSlug || slug}.png`;
  const price =
    treatment.pricing?.startingAt ??
    treatment.pricing?.options?.[0]?.price ??
    null;

  const offers = price
    ? treatment.pricing?.options
      ? {
          "@type": "AggregateOffer",
          priceCurrency: "USD",
          lowPrice: Math.min(
            ...treatment.pricing.options.map((o) => o.price),
          ),
          highPrice: Math.max(
            ...treatment.pricing.options.map((o) => o.price),
          ),
          offerCount: treatment.pricing.options.length,
          availability: "https://schema.org/InStock",
          url,
        }
      : {
          "@type": "Offer",
          priceCurrency: "USD",
          price,
          availability: "https://schema.org/InStock",
          url,
        }
    : undefined;

  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: treatment.title || treatment.name || treatment.label,
    description:
      treatment.metaDescription ||
      treatment.hero ||
      treatment.heroHeadline ||
      "Clinician-guided wellness care.",
    image: primaryImage,
    brand: { "@type": "Brand", name: "Melbi Wellness" },
    category: treatment.category || "Wellness / Prescription",
    url,
    ...(offers ? { offers } : {}),
  };
}

/**
 * FAQPage schema. `faqs` is an array of { q, a } (string or JSX) or { q, aText }.
 */
export function faqPageSchema(faqs) {
  if (!faqs || !faqs.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map(({ q, a, aText }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: {
        "@type": "Answer",
        text: aText ?? nodeText(a),
      },
    })),
  };
}

/**
 * MedicalWebPage schema for treatment pages — signals medical context to Google.
 */
export function medicalWebPageSchema({ name, description, url, lastReviewed, offers }) {
  const pageUrl = url.startsWith("http") ? url : `${ORIGIN}${url}`;
  return {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    name,
    description,
    url: pageUrl,
    publisher: { "@id": `${ORIGIN}/#organization` },
    ...(offers?.length
      ? {
          offers: offers.map((o) => ({
            "@type": "Offer",
            name: o.name,
            price: o.price,
            priceCurrency: "USD",
            priceSpecification: {
              "@type": "UnitPriceSpecification",
              price: o.price,
              priceCurrency: "USD",
              unitCode: "MON",
            },
            availability: "https://schema.org/InStock",
            url: pageUrl,
          })),
        }
      : {}),
    ...(lastReviewed ? { lastReviewed } : {}),
    audience: {
      "@type": "MedicalAudience",
      audienceType: "Patient",
      geographicArea: {
        "@type": "AdministrativeArea",
        name: "United States",
      },
    },
  };
}
